import { useQuery } from "@tanstack/react-query";
import api from "../../http";
import { Fertilizer } from "@practium/features/products";

interface PaginatedResponse {
    docs: Fertilizer[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
}

interface UseGetFertilizersParams {
    page?: number;
    limit?: number;
    filter?: string;
    category?: string | string[];
    manufacturer?: string | string[];
    nature?: string | string[];
    origin?: string | string[];
    sortAsc?: boolean;
    enabled?: boolean;
}

export const useGetFertilizers = ({
    page = 1,
    limit = 100,
    filter,
    category,
    nature,
    manufacturer,
    origin,
    sortAsc,
    enabled = true
}: UseGetFertilizersParams = {}) => {
    const query = useQuery<PaginatedResponse>({
        queryKey: ['fertilizers', page, limit, filter, category, nature, origin, sortAsc, manufacturer],
        enabled,
        placeholderData: (previousData) => previousData,
        queryFn: async (): Promise<PaginatedResponse> => {
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('limit', String(limit));

            if (filter) params.set('filter', filter);
            if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));

            const categories = Array.isArray(category) ? category : category ? [category] : [];
            const natures = Array.isArray(nature) ? nature : nature ? [nature] : [];
            const origins = Array.isArray(origin) ? origin : origin ? [origin] : [];
            const manufacturers = Array.isArray(manufacturer) ? manufacturer : manufacturer ? [manufacturer] : [];

            categories.forEach((item) => params.append('category', item));
            natures.forEach((item) => params.append('nature', item));
            origins.forEach((item) => params.append('origin', item));
            manufacturers.forEach((item) => params.append('manufacturer', item));

            const { data } = await api.get<PaginatedResponse>('/products/fertilizer/findAll', {
                params
            });
            return data;
        },
    });

    return {
        ...query,
        data: query.data?.docs ?? [],
        pagination: query.data ? {
            totalDocs: query.data.totalDocs,
            totalPages: query.data.totalPages,
            page: query.data.page,
            hasPrevPage: query.data.hasPrevPage,
            hasNextPage: query.data.hasNextPage,
            prevPage: query.data.prevPage,
            nextPage: query.data.nextPage,
        } : null,
    };
};
